import React from 'react';
import { Link } from 'react-router-dom';
import SEOHelmet from '../components/SEOHelmet';
import SkipToContent from '../components/SkipToContent';
import OtherStates from '../components/OtherStates';

interface ProfessionInfo {
  id: string;
  name: string;
  icon: string;
  credits: number;
  cycleYears: number;
  terminology: string;
  highlights: string[];
  stateBoard: string;
  boardUrl: string;
}

const SCLicenseRenewal: React.FC = () => {
  const professions: ProfessionInfo[] = [
    {
      id: 'ARCH',
      name: 'Architect',
      icon: '🏛️',
      credits: 12,
      cycleYears: 1,
      terminology: 'LU',
      highlights: [
        '12 LU annually (July 1 – June 30)',
        'Health, Safety & Welfare topics required',
        'AIA/CES or NCARB-approved providers',
        'Up to 4 LU self-directed learning',
        'License renewed annually with SCBAE'
      ],
      stateBoard: 'South Carolina Board of Architectural Examiners',
      boardUrl: 'https://llr.sc.gov/arch/'
    },
    {
      id: 'PE',
      name: 'Professional Engineer',
      icon: '⚙️',
      credits: 0,
      cycleYears: 2,
      terminology: 'PDH',
      highlights: [
        'South Carolina has no mandatory state CE for PEs',
        'NSPE recommends 30 PDH biennial',
        'Professional development strongly encouraged',
        'Ethics training recommended',
        'License renewed every 2 years'
      ],
      stateBoard: 'South Carolina Board of Registration for Professional Engineers and Surveyors',
      boardUrl: 'https://llr.sc.gov/engineer/'
    },
    {
      id: 'CPA',
      name: 'Certified Public Accountant',
      icon: '💼',
      credits: 40,
      cycleYears: 1,
      terminology: 'CPE',
      highlights: [
        '40 CPE hours per calendar year',
        '2 hours Ethics (mandatory)',
        'Min 20 hours per year in technical subjects',
        'NASBA-approved sponsors required',
        'Permit year: January 1 – December 31'
      ],
      stateBoard: 'South Carolina Board of Accountancy',
      boardUrl: 'https://llr.sc.gov/acct/'
    },
    {
      id: 'ATT',
      name: 'Attorney',
      icon: '⚖️',
      credits: 14,
      cycleYears: 1,
      terminology: 'CLE',
      highlights: [
        '14 CLE hours per year',
        '2 hours Ethics/Professional Responsibility (mandatory)',
        'At least 2 hours may be in other required subjects',
        'Max 4 self-study hours per year',
        'Annual reporting deadline: January 15'
      ],
      stateBoard: 'South Carolina Commission on CLE and Specialization',
      boardUrl: 'https://www.sccle.org/'
    },
    {
      id: 'RN',
      name: 'Registered Nurse',
      icon: '⚕️',
      credits: 0,
      cycleYears: 2,
      terminology: 'contact hours',
      highlights: [
        'South Carolina has no mandatory CE for RN renewal',
        'Competency maintained through clinical practice',
        'ANCC specialty certification recommended',
        'License renewed biennially',
        'Voluntary CE participation encouraged'
      ],
      stateBoard: 'South Carolina State Board of Nursing',
      boardUrl: 'https://llr.sc.gov/nurse/'
    },
    {
      id: 'PHARM',
      name: 'Pharmacist',
      icon: '💊',
      credits: 15,
      cycleYears: 1,
      terminology: 'CE',
      highlights: [
        '15 CE hours per year',
        '1 hour SC pharmacy law (mandatory)',
        '1 hour patient safety or medication error',
        'ACPE-accredited programs required',
        'License renewed annually'
      ],
      stateBoard: 'South Carolina Board of Pharmacy',
      boardUrl: 'https://llr.sc.gov/pharm/'
    }
  ];

  const structuredData = [
    {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "South Carolina Professional License Renewal Requirements",
      "url": "https://cectrack.com/sc-license-renewal",
      "breadcrumb": {
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://cectrack.com" },
          { "@type": "ListItem", "position": 2, "name": "South Carolina License Renewal", "item": "https://cectrack.com/sc-license-renewal" }
        ]
      }
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "How many CE hours do South Carolina architects need each year?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "South Carolina architects must complete 12 learning units (LU) annually from July 1 through June 30. Requirements include Health, Safety & Welfare topics from AIA/CES or NCARB-approved providers. Up to 4 LU of self-directed learning are allowed."
          }
        },
        {
          "@type": "Question",
          "name": "What are the CLE requirements for South Carolina attorneys?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "South Carolina attorneys must complete 14 CLE hours per year, including 2 hours of Ethics/Professional Responsibility. Up to 4 self-study hours are allowed annually. The reporting deadline is January 15 of the following year."
          }
        },
        {
          "@type": "Question",
          "name": "How many CPE hours do South Carolina CPAs require?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "South Carolina CPAs must complete 40 CPE hours per calendar year, including 2 hours of Ethics. At least 20 hours must be in technical subjects, and courses must be from NASBA-approved sponsors."
          }
        },
        {
          "@type": "Question",
          "name": "Which state agency handles professional licensing in South Carolina?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "The South Carolina Department of Labor, Licensing and Regulation (LLR) oversees most professional licenses including architects, engineers, CPAs, nurses, and pharmacists. The SC Commission on CLE and Specialization administers attorney continuing education requirements."
          }
        }
      ]
    }
  ];

  return (
    <>
      <SkipToContent />
      <SEOHelmet
        title="South Carolina Professional License Renewal | CE Requirements"
        description="Complete guide to South Carolina professional license renewal requirements. Track CE credits for Architects, CPAs, Attorneys, Pharmacists, and Engineers in South Carolina."
        keywords="South Carolina license renewal, SC CE requirements, South Carolina continuing education, SC professional licenses, SC LLR, South Carolina CPA CPE, SC CLE, SCBAE"
        canonical="https://cectrack.com/sc-license-renewal"
        ogImage="https://cectrack.com/og-image.png"
        structuredData={structuredData}
      />

      <section
        aria-label="Page header"
        style={{ background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)', color: 'white', padding: '80px 0 60px', position: 'relative', overflow: 'hidden' }}
      >
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundImage: "url(\"data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E\")", opacity: 0.4 }} />
        <div className="container" style={{ position: 'relative', zIndex: 1 }}>
          <div className="row justify-content-center text-center">
            <div className="col-lg-10">
              <h1 style={{ fontSize: '3rem', fontWeight: '700', marginBottom: '1.5rem', textShadow: '0 2px 4px rgba(0,0,0,0.2)' }}>
                South Carolina Professional License Renewal
              </h1>
              <p style={{ fontSize: '1.3rem', marginBottom: '2rem', opacity: 0.95 }}>
                Track CE requirements across all your South Carolina professional licenses in one place.
              </p>
              <div className="d-flex gap-3 justify-content-center flex-wrap">
                <Link to="/register" className="btn btn-light btn-lg px-5 py-3" style={{ fontWeight: '600', borderRadius: '8px' }}>Start Tracking Free</Link>
                <a href="#professions" className="btn btn-outline-light btn-lg px-5 py-3" style={{ fontWeight: '600', borderRadius: '8px' }}>View Requirements</a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section aria-label="Statistics" style={{ padding: '60px 0', backgroundColor: '#f8f9fa' }}>
        <div className="container">
          <div className="row g-4 text-center">
            <div className="col-md-3"><div className="p-4"><div style={{ fontSize: '3rem', fontWeight: '700', color: '#1e3a8a', marginBottom: '0.5rem' }}>6</div><div style={{ fontSize: '1.1rem', color: '#6c757d' }}>Professions Supported</div></div></div>
            <div className="col-md-3"><div className="p-4"><div style={{ fontSize: '3rem', fontWeight: '700', color: '#1e3a8a', marginBottom: '0.5rem' }}>50+</div><div style={{ fontSize: '1.1rem', color: '#6c757d' }}>Category Requirements</div></div></div>
            <div className="col-md-3"><div className="p-4"><div style={{ fontSize: '3rem', fontWeight: '700', color: '#1e3a8a', marginBottom: '0.5rem' }}>100%</div><div style={{ fontSize: '1.1rem', color: '#6c757d' }}>Board Compliant</div></div></div>
            <div className="col-md-3"><div className="p-4"><div style={{ fontSize: '3rem', fontWeight: '700', color: '#1e3a8a', marginBottom: '0.5rem' }}>24/7</div><div style={{ fontSize: '1.1rem', color: '#6c757d' }}>Access Anywhere</div></div></div>
          </div>
        </div>
      </section>

      <main id="main-content">
        <section id="professions" aria-labelledby="professions-heading" style={{ padding: '80px 0' }}>
          <div className="container">
            <div className="text-center mb-5">
              <h2 id="professions-heading" style={{ fontSize: '2.5rem', fontWeight: '700' }}>South Carolina CE Requirements by Profession</h2>
            </div>
            <div className="row g-4">
              {professions.map((prof) => (
                <div key={prof.id} className="col-md-6 col-lg-4">
                  <div
                    className="card h-100 border-0 shadow-sm"
                    style={{ transition: 'all 0.3s ease', cursor: 'pointer' }}
                    onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-5px)'; e.currentTarget.style.boxShadow = '0 8px 16px rgba(0,0,0,0.15)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.1)'; }}
                  >
                    <div className="card-body p-4">
                      <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>{prof.icon}</div>
                      <h5 style={{ fontSize: '1.3rem', fontWeight: '600', marginBottom: '1rem' }}>{prof.name}</h5>
                      <div style={{ backgroundColor: '#e7f3ff', padding: '12px 16px', borderRadius: '8px', marginBottom: '1rem' }}>
                        <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#1e3a8a' }}>
                          {prof.credits > 0 ? `${prof.credits} ${prof.terminology}` : 'No State Mandate'}
                        </div>
                        <div style={{ fontSize: '0.9rem', color: '#6c757d' }}>
                          {prof.cycleYears === 1 ? 'Annual' : prof.cycleYears === 2 ? 'Biennial' : 'Triennial'} Renewal
                        </div>
                      </div>
                      <div style={{ marginBottom: '1rem' }}>
                        <div style={{ fontSize: '0.85rem', fontWeight: '600', color: '#6c757d', marginBottom: '0.5rem', textTransform: 'uppercase' }}>Key Requirements:</div>
                        <ul style={{ fontSize: '0.9rem', margin: 0, paddingLeft: '1.2rem' }}>
                          {prof.highlights.map((h, i) => <li key={i} style={{ marginBottom: '0.3rem' }}>{h}</li>)}
                        </ul>
                      </div>
                      <div style={{ paddingTop: '1rem', borderTop: '1px solid #e9ecef', fontSize: '0.85rem', color: '#6c757d' }}>
                        <strong>{prof.stateBoard}</strong><br />
                        <a href={prof.boardUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#3b82f6', textDecoration: 'none' }}>Visit State Board →</a>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section style={{ padding: '80px 0', backgroundColor: '#f8f9fa' }}>
          <div className="container">
            <div className="row align-items-center">
              <div className="col-lg-6 mb-4 mb-lg-0">
                <h2 style={{ fontSize: '2.5rem', fontWeight: '700', marginBottom: '1.5rem' }}>Simplify Your South Carolina License Management</h2>
                <p style={{ fontSize: '1.1rem', color: '#6c757d', marginBottom: '2rem' }}>CEC Track automatically tracks your requirements, calculates your progress, and sends timely reminders so you never miss a renewal deadline.</p>
                <ul style={{ fontSize: '1.1rem', listStyle: 'none', padding: 0 }}>
                  {[
                    'Automated deadline tracking for all South Carolina professions',
                    'Upload certificates and match to requirements',
                    'Real-time progress reports by category',
                    'Multi-state support for professionals licensed in multiple states',
                    'Secure cloud storage for all documents',
                    'Email notifications for upcoming deadlines'
                  ].map((f, i) => (
                    <li key={i} style={{ marginBottom: '1rem', paddingLeft: '2rem', position: 'relative' }}>
                      <span style={{ position: 'absolute', left: 0, color: '#10b981', fontSize: '1.5rem' }}>✓</span>{f}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="col-lg-6">
                <div className="p-5 text-center" style={{ backgroundColor: 'white', borderRadius: '16px', boxShadow: '0 10px 30px rgba(0,0,0,0.1)' }}>
                  <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>📱</div>
                  <h3 style={{ fontSize: '1.8rem', fontWeight: '700', marginBottom: '1rem' }}>Start Tracking Today</h3>
                  <p style={{ color: '#6c757d', marginBottom: '2rem' }}>Join professionals across South Carolina who trust CEC Track to manage their continuing education.</p>
                  <Link to="/register" className="btn btn-primary btn-lg px-5 py-3" style={{ fontWeight: '600', background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)', border: 'none', borderRadius: '8px' }}>Create Free Account</Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section aria-labelledby="resources-heading" style={{ padding: '80px 0' }}>
          <div className="container">
            <div className="text-center mb-5">
              <h2 id="resources-heading" style={{ fontSize: '2.5rem', fontWeight: '700' }}>South Carolina State Resources</h2>
            </div>
            <div className="row g-4">
              <div className="col-md-6">
                <article className="card border-0 shadow-sm h-100">
                  <div className="card-body p-4">
                    <h3 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '1rem' }}>SC Department of Labor, Licensing and Regulation</h3>
                    <p style={{ color: '#6c757d', marginBottom: '1rem' }}>Regulates architects, engineers, CPAs, nurses, and pharmacists in South Carolina.</p>
                    <div style={{ marginBottom: '0.5rem' }}><strong>Phone:</strong> <a href="tel:+18032128888">(803) 896-4300</a></div>
                    <div style={{ marginBottom: '0.5rem' }}><strong>Website:</strong> <a href="https://llr.sc.gov/" target="_blank" rel="noopener noreferrer">llr.sc.gov</a></div>
                    <div style={{ marginBottom: '1rem' }}><strong>Office:</strong> 110 Centerview Dr, Columbia, SC 29210</div>
                    <a href="https://llr.sc.gov/" target="_blank" rel="noopener noreferrer" className="btn btn-outline-primary">View All Professions</a>
                  </div>
                </article>
              </div>
              <div className="col-md-6">
                <article className="card border-0 shadow-sm h-100">
                  <div className="card-body p-4">
                    <h3 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '1rem' }}>SC Commission on CLE and Specialization</h3>
                    <p style={{ color: '#6c757d', marginBottom: '1rem' }}>Administers continuing legal education requirements for South Carolina attorneys.</p>
                    <div style={{ marginBottom: '0.5rem' }}><strong>Phone:</strong> <a href="tel:+18037343678">(803) 734-3678</a></div>
                    <div style={{ marginBottom: '0.5rem' }}><strong>Website:</strong> <a href="https://www.sccle.org/" target="_blank" rel="noopener noreferrer">sccle.org</a></div>
                    <div style={{ marginBottom: '1rem' }}><strong>Office:</strong> PO Box 11297, Columbia, SC 29211</div>
                    <a href="https://www.sccle.org/" target="_blank" rel="noopener noreferrer" className="btn btn-outline-primary">View CLE Requirements</a>
                  </div>
                </article>
              </div>
            </div>
          </div>
        </section>

        <OtherStates currentState="SC" />

        <section aria-label="Call to action" style={{ background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)', color: 'white', padding: '80px 0' }}>
          <div className="container text-center">
            <h2 style={{ fontSize: '2.5rem', fontWeight: '700', marginBottom: '1.5rem' }}>Ready to Simplify Your South Carolina License Renewal?</h2>
            <p style={{ fontSize: '1.25rem', marginBottom: '2rem', opacity: 0.95 }}>Join professionals across South Carolina who never worry about missing CE requirements</p>
            <Link to="/register" className="btn btn-light btn-lg px-5 py-3" style={{ fontWeight: '600', borderRadius: '8px' }}>Get Started Free</Link>
          </div>
        </section>
      </main>
    </>
  );
};

export default SCLicenseRenewal;
